import { ReturnModalShell } from "@dana/Components/StandardReturns/ReturnModalShell";

import {
    FreightCollectInfo,
    ShippingContact,
    UpdateStandardReturnStateParameter,
    WizardStepKey,
} from "@dana/Services/DanaStandardReturnOrderService";

import StepConfirmItems from "@dana/Components/StandardReturns/StepConfirmItems";
import StepEmailContacts from "@dana/Components/StandardReturns/StepEmailContacts";
import StepFreightCollect from "@dana/Components/StandardReturns/StepFreightCollect";
import StepInternalRefOnly from "@dana/Components/StandardReturns/StepInternalRefOnly";
import StepReasonDisposition from "@dana/Components/StandardReturns/StepReasonDisposition";
import StepReview from "@dana/Components/StandardReturns/StepReview";
import StepSelectItems from "@dana/Components/StandardReturns/StepSelectItems";
import StepShippingContact from "@dana/Components/StandardReturns/StepShippingContact";
import StepSubmitResult from "@dana/Components/StandardReturns/StepSubmitResult";
import { themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import resetStandardReturnState from "@dana/Store/Data/StandardReturns/Handlers/ResetStandardReturnStateHandler";
import submitStandardReturnOrder, {
    SubmitStandardReturnOrderParameter,
} from "@dana/Store/Data/StandardReturns/Handlers/SubmitStandardReturnOrderHandler";
import updateStep from "@dana/Store/Data/StandardReturns/Handlers/UpdateCurrentStepHandler";
import updateDisposition from "@dana/Store/Data/StandardReturns/Handlers/UpdateDispositionHandler";
import updateEmails from "@dana/Store/Data/StandardReturns/Handlers/UpdateEmailsHandler";
import updateFreightCollectInfo from "@dana/Store/Data/StandardReturns/Handlers/UpdateFreightCollectInfoHandler";
import updateInternalRefNumber from "@dana/Store/Data/StandardReturns/Handlers/UpdateInternalRefHandler";
import updateQuery from "@dana/Store/Data/StandardReturns/Handlers/UpdateQueryHandler";
import updateReason from "@dana/Store/Data/StandardReturns/Handlers/UpdateReasonHandler";
import updateSelectedLines from "@dana/Store/Data/StandardReturns/Handlers/UpdateSelectedLinesHandler";
import updateShippingContact from "@dana/Store/Data/StandardReturns/Handlers/UpdateShippingContactHandler";
import updateSubmitStatus from "@dana/Store/Data/StandardReturns/Handlers/UpdateSubmitStatusHandler";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useMemo } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// --- Styles Interfaces and Objects ---
export interface ReturnWizardModalStyles {
    modal?: ModalPresentationProps;
    headerMetaWrapper?: InjectableCss;
    stepButtons?: InjectableCss;
}

export const ReturnWizardModalStyles: ReturnWizardModalStyles = {
    modal: {
        size: 1140,
        sizeVariant: "large",
    },
    headerMetaWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 32px;
            margin-bottom: 16px;

            .order-no-wrapper {
                padding: 12px;
                border: 1px dashed ${themeColors.primary.richBlack};
                border-radius: 4px;
                display: flex;
                align-items: center;
                gap: 16px;

                .order-no-box {
                    display: flex;
                    align-items: center;
                    gap: 8px;

                    .meta-title {
                        color: ${themeBackgrounds.darkGray};
                    }
                }
            }

            .search-wrapper {
                flex-grow: 1;
            }
        `,
    },
    stepButtons: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin: 24px 0 0;
        `,
    },
};

const styles = ReturnWizardModalStyles;
// --- Props Interface ---
interface ReturnWizardModalProps {
    isModalOpen: boolean;
    handleModalclose: () => void;
    Modal: React.ComponentType<any>;
}
// --- Component Definition ---
const mapStateToProps = (state: ApplicationStateDana) => {
    return {
        standardReturnSate: state.standardReturnState,
    };
};
const mapDispatchToProps = {
    updateEmails,
    updateFreightCollectInfo,
    updateShippingContact,
    updateInternalRefNumber,
    updateDisposition,
    updateReason,
    updateQuery,
    updateSubmitStatus,
    updateSelectedLines,
    updateStep,
    submitStandardReturnOrder,
    resetStandardReturnState,
};
type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & ReturnWizardModalProps;

const ReturnWizardModal: React.FC<Props> = ({
    Modal,
    isModalOpen,
    handleModalclose,
    standardReturnSate,
    updateEmails,
    updateFreightCollectInfo,
    updateShippingContact,
    updateInternalRefNumber,
    updateDisposition,
    updateReason,
    updateQuery,
    updateSubmitStatus,
    updateSelectedLines,
    updateStep,
    submitStandardReturnOrder,
    resetStandardReturnState,
}) => {
    // const [currentStep, setCurrentStep] = useState<WizardStepKey>("SELECT_ITEMS");

    const selectedForReturn = useMemo(
        () => standardReturnSate?.selectedLines?.filter(l => l?.return && l?.returnItemQty > 0),
        [standardReturnSate?.selectedLines],
    );

    const stepFlow: WizardStepKey[] = useMemo(() => {
        const base: WizardStepKey[] = ["SELECT_ITEMS", "CONFIRM_ITEMS", "REASON_DISPOSITION"];
        if (standardReturnSate?.disposition === "SCRAP_AT_DANA_LOCATION") {
            base.push("INTERNAL_REF_ONLY");
        } else {
            base.push("SHIPPING_CONTACT", "FREIGHT_COLLECT");
        }
        base.push("EMAIL_CONTACTS", "REVIEW", "SUBMIT_RESULT");
        return base;
    }, [standardReturnSate?.disposition]);

    // Step validations
    const canProceed = useMemo(() => {
        switch (standardReturnSate?.currentStep) {
            case "SELECT_ITEMS":
                return (
                    selectedForReturn?.length > 0 &&
                    selectedForReturn?.every(l => l?.returnItemQty >= 1 && l?.returnItemQty <= l?.qtyShipped)
                );
            case "CONFIRM_ITEMS":
                return true;
            case "REASON_DISPOSITION":
                return !!(standardReturnSate?.reason && standardReturnSate?.disposition);
            case "INTERNAL_REF_ONLY":
                return true;
            case "SHIPPING_CONTACT": {
                const c = standardReturnSate?.shippingContact;
                if (!c) {
                    return false;
                }
                const filled = [c.firstName, c.phone, c.email, c.lastName].every(Boolean);
                const emailOk = /\S+@\S+\.\S+/.test(c.email);
                return filled && emailOk;
            }
            case "FREIGHT_COLLECT": {
                const f = standardReturnSate?.freightCollectInfo;
                return !!(
                    f &&
                    f.customerName &&
                    f.country &&
                    f.zipCode &&
                    f.state &&
                    f.city &&
                    f.address1 &&
                    f.freightHandling &&
                    f.carrier &&
                    f.carrierAccountNo
                );
            }
            case "EMAIL_CONTACTS":
                return (
                    standardReturnSate?.emailContacts.length <= 5 &&
                    standardReturnSate?.emailContacts.every(e => /\S+@\S+\.\S+/.test(e))
                );
            case "REVIEW":
            case "SUBMIT_RESULT":
                return true;
            default:
                return false;
        }
    }, [standardReturnSate?.currentStep, selectedForReturn, standardReturnSate]);

    // Navigation
    const goNext = () => {
        const idx = stepFlow.indexOf(standardReturnSate?.currentStep);
        if (idx === -1) {
            return updateStep({ currentStep: stepFlow[0] } as UpdateStandardReturnStateParameter);
        }
        if (idx < stepFlow.length - 1) {
            updateStep({ currentStep: stepFlow[idx + 1] } as UpdateStandardReturnStateParameter);
        }
    };
    const goBack = () => {
        const idx = stepFlow.indexOf(standardReturnSate?.currentStep);
        if (idx > 0) {
            updateStep({ currentStep: stepFlow[idx - 1] } as UpdateStandardReturnStateParameter);
        }
    };
    const goTo = (s: WizardStepKey) => updateStep({ currentStep: s } as UpdateStandardReturnStateParameter);

    // Actions
    // Programmed as per demo data - Update the logic as per real data
    const toggleReturn = (lineNo: string) => {
        updateSelectedLines({
            selectedLines: standardReturnSate?.selectedLines.map(l => {
                if (l.lineNumber.toString() === lineNo) {
                    const canReturn = l.qtyShipped > 0;
                    const willReturn = canReturn ? !l.return : false;
                    return {
                        ...l,
                        return: willReturn,
                        returnItemQty: willReturn
                            ? Math.min(Math.max(l.returnItemQty || l.multipleSaleQty, 1), l.qtyShipped)
                            : 0,
                    };
                }
                return l;
            }),
        } as UpdateStandardReturnStateParameter);
    };
    // Programmed as per demo data - Update the logic as per real data
    const setReturnQty = (lineNo: string, qty: number) => {
        updateSelectedLines({
            selectedLines: standardReturnSate?.selectedLines.map(l => {
                if (l.lineNumber.toString() === lineNo) {
                    // Clamp between 0 and l.qtyShipped
                    const safe = Math.max(0, Math.min(qty, l.qtyShipped));
                    let validQty = safe;
                    if (l.multipleSaleQty > 1) {
                        // Only allow multiples
                        if (l.multipleSaleQty > 1) {
                            // Round down to the nearest multiple of multipleSaleQty
                            let roundedUp = Math.ceil(safe / l.multipleSaleQty) * l.multipleSaleQty;
                            validQty = Math.min(roundedUp, l.qtyShipped);
                            // validQty = Math.floor(safe / l.multipleSaleQty) * l.multipleSaleQty;
                        }
                    }
                    return { ...l, returnItemQty: validQty };
                }
                return l;
            }),
        } as UpdateStandardReturnStateParameter);
    };

    const submitReturnRequest = () => {
        updateSubmitStatus({ submitStatus: "SUBMITTING" } as UpdateStandardReturnStateParameter);
        submitStandardReturnOrder({
            selectedItems: standardReturnSate.selectedLines?.filter(x => x.returnItemQty > 0),
            shippingContact: standardReturnSate.shippingContact,
            freightCollectInfo:
                standardReturnSate.disposition !== "SCRAP_AT_DANA_LOCATION"
                    ? standardReturnSate.freightCollectInfo
                    : {},
            reason: standardReturnSate.reason,
            disposition: standardReturnSate.disposition,
            internalRefNumber: standardReturnSate.internalRefNumber,
            emailContacts: standardReturnSate.emailContacts,
            originalOrderNumber: standardReturnSate.selectedOrder?.erpOrderNumber,
            onSuccess: (respponse: any) => {
                updateSubmitStatus({ submitStatus: "SUCCESS" } as UpdateStandardReturnStateParameter);
            },
            onError: (error: string) => {

                updateSubmitStatus({ submitStatus: "FAILED" } as UpdateStandardReturnStateParameter);
            },
        } as SubmitStandardReturnOrderParameter);
    };

    const generateRmaAndRedirect = async () => {
        // Simulate generate & redirect
        // Programmed as per demo data - Update the logic as per real data
        await new Promise(res => setTimeout(res, 600));
    };

    // Render current step
    const renderStep = (s: WizardStepKey) => {
        switch (s) {
            case "SELECT_ITEMS":
                return (
                    <StepSelectItems
                        onChangeQuery={q => updateQuery({ filteredQuery: q } as UpdateStandardReturnStateParameter)}
                        onToggleReturn={toggleReturn}
                        onSetReturnQty={setReturnQty}
                    />
                );
            case "CONFIRM_ITEMS":
                return <StepConfirmItems selectedForReturn={selectedForReturn} />;
            case "REASON_DISPOSITION":
                return (
                    <StepReasonDisposition
                        setReason={(r: any) => updateReason({ reason: r } as UpdateStandardReturnStateParameter)}
                        setDisposition={(d: string) =>
                            updateDisposition({ disposition: d } as UpdateStandardReturnStateParameter)
                        }
                    />
                );
            case "INTERNAL_REF_ONLY":
                return (
                    <StepInternalRefOnly
                        internalRefNumber={standardReturnSate?.internalRefNumber || ""}
                        onChangeInternalRef={v =>
                            updateInternalRefNumber({ internalRefNumber: v } as UpdateStandardReturnStateParameter)
                        }
                    />
                );
            case "SHIPPING_CONTACT":
                return (
                    <StepShippingContact
                        setShippingContact={(c: ShippingContact) =>
                            updateShippingContact({ shippingContact: c } as UpdateStandardReturnStateParameter)
                        }
                        onChangeInternalRef={v =>
                            updateInternalRefNumber({ internalRefNumber: v } as UpdateStandardReturnStateParameter)
                        }
                    />
                );
            case "FREIGHT_COLLECT":
                return (
                    <StepFreightCollect
                        setFreight={(f: FreightCollectInfo) =>
                            updateFreightCollectInfo({ freightCollectInfo: f } as UpdateStandardReturnStateParameter)
                        }
                        onChangeInternalRef={v =>
                            updateInternalRefNumber({ internalRefNumber: v } as UpdateStandardReturnStateParameter)
                        }
                    />
                );
            case "EMAIL_CONTACTS":
                return (
                    <StepEmailContacts
                        addEmails={(newOnes: string[]) =>
                            updateEmails({
                                ...standardReturnSate,
                                emailContacts: [...standardReturnSate.emailContacts, ...newOnes],
                            } as UpdateStandardReturnStateParameter)
                        }
                        removeEmail={idx =>
                            updateEmails({
                                ...standardReturnSate,
                                emailContacts: standardReturnSate?.emailContacts.filter((_: any, i: any) => i !== idx),
                            } as UpdateStandardReturnStateParameter)
                        }
                    />
                );
            case "REVIEW":
                return (
                    <StepReview
                        items={selectedForReturn}
                        internalRefNumber={
                            standardReturnSate?.internalRefNumber ||
                            standardReturnSate?.freightCollectInfo?.internalRefNo
                        }
                        goTo={goTo}
                    />
                );
            case "SUBMIT_RESULT":
                return <StepSubmitResult onGenerateRma={generateRmaAndRedirect} />;
            default:
                return null;
        }
    };

    const isFirst = stepFlow.indexOf(standardReturnSate?.currentStep) === 0;
    const isLast = standardReturnSate?.currentStep === "SUBMIT_RESULT";
    const primaryLabel = standardReturnSate?.currentStep === "REVIEW" ? "Submit Return" : isLast ? undefined : "Next";

    const onPrimary = () => {
        // Programmed as per demo data - Update the logic as per real data
        if (standardReturnSate?.currentStep === "REVIEW") {
            submitReturnRequest();
        }
        goNext();
    };

    return (
        <ReturnModalShell
            Modal={Modal}
            styles={styles}
            isOpen={isModalOpen}
            onClose={handleModalclose}
            currentStep={standardReturnSate?.currentStep}
        >
            <StyledWrapper>{renderStep(standardReturnSate?.currentStep)}</StyledWrapper>

            <StyledWrapper {...styles.stepButtons}>
                {!isFirst && !isLast && <Button onClick={goBack}>{translate("Back")}</Button>}
                {primaryLabel && (
                    <Button
                        disabled={!canProceed || standardReturnSate?.submitStatus === "SUBMITTING"}
                        onClick={onPrimary}
                    >
                        {primaryLabel}
                    </Button>
                )}
            </StyledWrapper>
        </ReturnModalShell>
    );
};
export default connect(mapStateToProps, mapDispatchToProps)(ReturnWizardModal);
